import React, { useState, useRef, useEffect } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import VideoTranscriptionTab from "./tabs/VideoTranscriptionTab/VideoTranscriptionTab";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const VideoEditorSidebar: React.FC = () => {
  const [value, setValue] = useState(0);

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();
  
  // Stopwatch to track how long a user stays on a specific tab
  const tabFocusStartTime = useRef<number>(Date.now());

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // TELEMETRY: Calculate how long they spent on the PREVIOUS tab before switching
    const dwellTimeSeconds = (Date.now() - tabFocusStartTime.current) / 1000;
    
    if (dwellTimeSeconds > 5 && reportBehavior) {
      // If they spent more than 5 seconds, log it as meaningful interest in that specific tool
      reportBehavior(`tab_${value}_focus`, dwellTimeSeconds * 0.5, "interest");
    }

    // Update state to new tab
    setValue(newValue);
    tabFocusStartTime.current = Date.now(); // Reset the stopwatch

    // TELEMETRY: Log the intent of exploring a new tab
    if (reportBehavior) {
      reportBehavior(`sidebar_tab_selected_${newValue}`, 2, "interest");
    }
  };

  // Clean up dwell time if the component unmounts (e.g., sidebar closes)
  useEffect(() => {
    return () => {
      const dwellTimeSeconds = (Date.now() - tabFocusStartTime.current) / 1000;
      if (dwellTimeSeconds > 5 && reportBehavior) {
        reportBehavior(`tab_${value}_focus`, dwellTimeSeconds * 0.5, "interest");
      }
    };
  }, [value, reportBehavior]);

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;
  
  // Dynamic CSS Variables
  const borderColor = isDarkMode ? "rgba(255, 255, 255, 0.1)" : "divider";
  const activeIndicatorColor = isDarkMode ? "#00e5ff" : "primary.main";
  const tabTextColor = isDarkMode ? "rgba(255, 255, 255, 0.7)" : "text.secondary";
  const activeTabTextColor = isDarkMode ? "#ffffff" : "primary.main";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        // Smooth background transition inherited from the wrapper
        transition: "background-color 0.4s ease, color 0.4s ease",
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: borderColor, transition: "border-color 0.4s ease" }}>
        <Tabs
          scrollButtons="auto"
          value={value}
          onChange={handleChange}
          aria-label="Video Editor Sidebar Tabs"
          TabIndicatorProps={{
            sx: {
              backgroundColor: activeIndicatorColor,
              transition: "background-color 0.4s ease",
            }
          }}
        >
          <Tab 
            label="Transcript" 
            {...a11yProps(0)} 
            sx={{
              color: value === 0 ? activeTabTextColor : tabTextColor,
              transition: "color 0.4s ease",
              "&.Mui-selected": {
                color: activeTabTextColor,
                // ADAPTIVE UI: Gentle text pulse if user is frustrated to maintain spatial awareness
                animation: highlightCTA ? "pulse-text 2s infinite" : "none",
              },
              "@keyframes pulse-text": {
                "0%": { opacity: 1 },
                "50%": { opacity: 0.7 },
                "100%": { opacity: 1 }
              }
            }}
          />
          {/* Future tabs (e.g., AI Insights, Assets) go here */}
        </Tabs>
      </Box>
      <VideoTranscriptionTab value={value} index={0} />
    </Box>
  );
};

export default VideoEditorSidebar;